import React from 'react';
import {Typography} from 'antd';
import {OpenAPIRef} from './openapi-ref';

// SchemaPropType renders the "type summary" at the end of a property name,
// e.g.  "string", "array of Foo", "object with ALL properties of Bar".
//
// OpenAPI 3.0 expressed nullability via `nullable: true` sibling to `type`.
// OpenAPI 3.1 (JSON Schema 2020-12) dropped the `nullable` keyword in favor
// of a type-array: `type: ['string', 'null']`. Both are normalized here so
// the rest of the switch handles a single primary type string, and a
// "(nullable)" suffix is appended once at the end when either shape is
// detected.
export function SchemaPropType({record}) {
    if (!record) {
        return null;
    }

    const typeIsArray = Array.isArray(record.type);
    const isNullable = !!record.nullable || (typeIsArray && record.type.includes('null'));
    const primaryType = typeIsArray ? record.type.find((t) => t !== 'null') || record.type[0] : record.type;

    // Work off a shallow clone that carries the normalized single-string type,
    // so the switch below is identical to the pre-3.1 code path.
    const r = typeIsArray ? {...record, type: primaryType} : record;

    const body = renderBody(r);

    if (isNullable) {
        return (
            <>
                {body} <Typography.Text type="secondary">(nullable)</Typography.Text>
            </>
        );
    }
    return body;
}

function renderBody(record) {
    switch (record.type) {
        case 'array':
            if (record.subType === 'allOf') {
                return (
                    <Typography.Text strong>
                        array with ALL items of <OpenAPIRef link={record.subTypeDetails} />
                    </Typography.Text>
                );
            } else if (record.subType === 'anyOf') {
                return (
                    <Typography.Text strong>
                        array with ANY items of <OpenAPIRef link={record.subTypeDetails} />
                    </Typography.Text>
                );
            } else if (record.subType === 'oneOf') {
                return (
                    <Typography.Text strong>
                        array with ONE items of <OpenAPIRef link={record.subTypeDetails} />
                    </Typography.Text>
                );
            } else if (record.$ref) {
                return (
                    <Typography.Text strong>
                        array of items inheriting <OpenAPIRef link={record.$ref} />
                    </Typography.Text>
                );
            } else if (record.items) {
                if (record.items.$ref) {
                    return (
                        <Typography.Text strong>
                            array of <OpenAPIRef link={record.items.$ref} alt="type" />
                        </Typography.Text>
                    );
                } else if (record.items.type === 'object' || record.items.type === 'array') {
                    return <Typography.Text strong>array of {record.items.title || record.items.type}</Typography.Text>;
                } else {
                    return (
                        <Typography.Text strong>
                            array of <strong>{record.items.title || record.items.type || 'object'}</strong>
                        </Typography.Text>
                    );
                }
            } else {
                return (
                    <Typography.Text strong>
                        array of <strong>object</strong>
                    </Typography.Text>
                );
            }
        case 'object':
            if (record.subType === 'allOf') {
                return (
                    <Typography.Text strong>
                        object with ALL properties of <OpenAPIRef link={record.subTypeDetails} />
                    </Typography.Text>
                );
            } else if (record.subType === 'anyOf') {
                return (
                    <Typography.Text strong>
                        object with ANY properties of <OpenAPIRef link={record.subTypeDetails} />
                    </Typography.Text>
                );
            } else if (record.subType === 'oneOf') {
                return (
                    <Typography.Text strong>
                        object with ONE properties of <OpenAPIRef link={record.subTypeDetails} />
                    </Typography.Text>
                );
            } else if (record.$ref) {
                return (
                    <Typography.Text strong>
                        <OpenAPIRef link={record.$ref} />
                    </Typography.Text>
                );
            } else {
                return <Typography.Text strong>{record.type || 'unspecified'}</Typography.Text>;
            }
        default:
            return (
                <Typography.Text strong>
                    {record.type || 'unspecified'}
                    {record.format && <span>({record.format})</span>}
                </Typography.Text>
            );
    }
}
